import { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import {
  usePlay,
  usePlayMutations,
  useRunAgent,
  type PlayDetail,
  type StepRow,
  type StepEventRow,
} from "../../hooks/usePlay";
import { usePersona, type Persona } from "../../hooks/usePersona";
import { OutcomeHeader } from "../../components/OutcomeHeader";
import { useMe } from "../../hooks/useMe";
import {
  useWorkspaceEvents,
  type WorkspaceEvent,
} from "../../hooks/useWorkspaceEvents";
import { usePlannerStore } from "../../state/usePlannerStore";
import { projectDraft } from "./planner/projector";
import { StepDetail } from "../../components/StepDetail";
import { AgentMatchModal } from "../../components/AgentMatchModal";

type Tab = "steps" | "timeline" | "outcome";

function statusPill(state: string): string {
  if (state === "running") return "green";
  if (state === "paused") return "amber";
  if (state === "replanning") return "purple";
  if (state === "completed") return "blue";
  if (state === "abandoned") return "red";
  return "gray";
}


function stepTypeIcon(type: string): string {
  switch (type) {
    case "decision":
      return "◆";
    case "comm":
      return "✉";
    case "query":
      return "?";
    case "milestone":
      return "◇";
    case "wait":
      return "⌛";
    case "instrumentation":
      return "⟳";
    case "micro_task":
    default:
      return "◦";
  }
}

function elapsedLabel(p: PlayDetail["play"]): string {
  if (!p.startedAt) return "—";
  const ms = Date.now() - new Date(p.startedAt).getTime();
  if (ms < 0) return "—";
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 48) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `${days}d`;
}

function windowLabel(p: PlayDetail["play"]): string {
  if (!p.targetWindowEnd) return "—";
  try {
    return new Date(p.targetWindowEnd).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  } catch {
    return p.targetWindowEnd;
  }
}

interface ConfirmActionState {
  kind: "approve" | "start" | "pause" | "resume" | "abandon";
  needsRationale: boolean;
}

interface ActivityItem {
  id: string;
  time: string;
  who: string;
  text: string;
}

function eventToActivity(e: WorkspaceEvent, idx: number): ActivityItem {
  const p = (e.payload ?? {}) as Record<string, unknown>;
  const who =
    (typeof p["actorName"] === "string" && p["actorName"]) ||
    (typeof p["actorKind"] === "string" && p["actorKind"]) ||
    "System";
  const subject =
    (typeof p["title"] === "string" && p["title"]) ||
    (typeof p["stepId"] === "string" && `step ${p["stepId"]}`) ||
    "";
  return {
    id: `evt-${Date.now()}-${idx}`,
    time: new Date().toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    }),
    who: String(who),
    text: `${e.type} ${subject}`,
  };
}

interface CxoActionsProps {
  state: string;
  persona: Persona | null;
  onApprove: () => void;
  onStart: () => void;
  onPause: () => void;
  onResume: () => void;
  onAbandon: () => void;
  onEdit: () => void;
  pending: boolean;
}

function CxoActions({
  state,
  persona,
  onApprove,
  onStart,
  onPause,
  onResume,
  onAbandon,
  onEdit,
  pending,
}: CxoActionsProps) {
  if (persona !== "cxo" && persona !== "owner") return null;
  return (
    <div
      style={{
        display: "flex",
        gap: 6,
        alignItems: "center",
        flexWrap: "wrap",
      }}
      data-testid="cxo-actions"
    >
      {state === "draft" ? (
        <>
          <button
            type="button"
            className="btn primary"
            onClick={onApprove}
            disabled={pending}
            data-testid="cxo-approve-btn"
            data-tip="Lock in this plan and pass it to the execution team"
          >
            Approve
          </button>
          <button
            type="button"
            className="btn"
            onClick={onEdit}
            disabled={pending}
            data-testid="cxo-edit-btn"
            data-tip="Open the editor to revise tasks before approving."
          >
            Edit
          </button>
        </>
      ) : null}
      {state === "approved" ? (
        <button
          type="button"
          className="btn primary"
          onClick={onStart}
          disabled={pending}
          data-testid="cxo-start-btn"
          data-tip="Kick off execution — agents and operators are notified and steps become claimable"
        >
          Start
        </button>
      ) : null}
      {state === "running" ? (
        <button
          type="button"
          className="btn"
          onClick={onPause}
          disabled={pending}
          data-testid="cxo-pause-btn"
          data-tip="Pause all execution — no new steps will be dispatched until you resume"
        >
          Pause
        </button>
      ) : null}
      {state === "paused" ? (
        <button
          type="button"
          className="btn primary"
          onClick={onResume}
          disabled={pending}
          data-testid="cxo-resume-btn"
          data-tip="Resume execution from where it left off"
        >
          Resume
        </button>
      ) : null}
      {state !== "completed" && state !== "abandoned" ? (
        <>
          <button
            type="button"
            className="btn"
            onClick={onAbandon}
            disabled={pending}
            data-testid="cxo-abandon-btn"
            data-tip="Cancel this routine permanently."
          >
            Abandon
          </button>
        </>
      ) : null}
    </div>
  );
}

interface TimelineNode {
  key: string;
  at: number;
  label: string;
  owner: string | null;
  isAutonomous: boolean;
  date: string;
  done: boolean;
  major: boolean;
}

const tlDate = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
});

function buildTimelineNodes(detail: PlayDetail): TimelineNode[] {
  const { play, steps } = detail;
  const total = steps.length;
  if (total === 0) return [];

  const startMs = play.startedAt
    ? new Date(play.startedAt).getTime()
    : new Date(play.createdAt).getTime();
  const endMs = play.targetWindowEnd
    ? new Date(play.targetWindowEnd).getTime()
    : startMs + total * 86_400_000;
  const span = Math.max(endMs - startMs, 1);

  const placeAt = (s: StepRow): number => {
    const ts = s.completedAt ?? s.startedAt;
    if (ts) {
      const v = (new Date(ts).getTime() - startMs) / span;
      return Math.max(0, Math.min(1, v));
    }
    if (total === 1) return 0;
    return (s.ord - 1) / Math.max(total - 1, 1);
  };

  const stepNodes: TimelineNode[] = steps.map((s) => {
    const t = s.completedAt ?? s.startedAt ?? null;
    return {
      key: `step-${s.id}`,
      at: placeAt(s),
      label: s.title,
      owner: s.displayOwner,
      isAutonomous: s.isAutonomous,
      date: t ? tlDate.format(new Date(t)) : "—",
      done: s.state === "completed",
      major: s.type === "milestone",
    };
  });

  return stepNodes.sort((a, b) => a.at - b.at);
}

function PlayTimeline({ detail }: { detail: PlayDetail }) {
  const nodes = useMemo(() => buildTimelineNodes(detail), [detail]);
  const stepsTotal = detail.steps.length;
  const stepsDone = detail.steps.filter((s) => s.state === "completed").length;
  const progress = stepsTotal > 0 ? stepsDone / stepsTotal : 0;

  if (nodes.length === 0) {
    return (
      <div
        className="play-timeline-empty"
        data-testid="play-timeline-empty" style={{ color: "var(--text-3)", textAlign: "center" }}
      >
        No tasks yet — once the routine has a task graph, this view shows the milestone strip.
      </div>
    );
  }

  return (
    <div className="play-tl-wrap">
      <div
        className="play-tl-rail"
        style={{
          position: "relative",
          height: 64,
          display: "flex",
          alignItems: "center",
          borderTop: "1px solid var(--border)",
          borderBottom: "1px solid var(--border)",
          padding: "0 8px",
          background: "var(--bg)",
        }}
      >
        <div
          style={{
            position: "absolute",
            left: 20,
            right: 20,
            top: "50%",
            height: 2,
            background: "var(--border)",
          }}
        />
        <div
          style={{
            position: "absolute",
            left: 20,
            top: "50%",
            height: 2,
            background: "var(--accent)",
            width: `calc((100% - 40px) * ${progress})`,
            transition: "width 240ms ease",
          }}
        />
        {nodes.map((n) => {
          const size = n.major ? 14 : 10;
          return (
            <div
              key={n.key}
              title={n.label}
              style={{
                position: "absolute",
                left: `calc(20px + (100% - 40px) * ${n.at})`,
                top: "50%",
                transform: "translate(-50%, -50%)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                zIndex: 1,
              }}
            >
              <div
                style={{
                  width: size,
                  height: size,
                  borderRadius: "50%",
                  background: n.done ? "var(--accent)" : "white",
                  border: n.done ? "none" : "2px solid var(--border-strong)",
                  color: "white",
                  fontSize: 8,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              />
            </div>
          );
        })}
      </div>
      <div style={{ position: "relative", height: 60, marginTop: 4 }}>
        {nodes.map((n) => (
          <div
            key={`l-${n.key}`}
            style={{
              position: "absolute",
              left: `calc(20px + (100% - 40px) * ${n.at})`,
              transform: "translateX(-50%)",
              textAlign: "center",
              maxWidth: 130,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 500,
                color:
                  "var(--text)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: 130,
                display: "inline-flex",
                alignItems: "center",
                gap: 3,
                justifyContent: "center",
              }}
            >
              {n.isAutonomous ? (
                <span
                  title="Autonomous — runs unattended" style={{ color: "var(--accent)" }}
                  aria-label="autonomous"
                >
                  ⚡
                </span>
              ) : null}
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                {n.label}
              </span>
            </div>
            {n.owner ? (
              <div
                style={{
                  fontSize: 10,
                  color: "var(--text-3)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: 130,
                }}
                title={n.owner}
              >
                {n.owner}
              </div>
            ) : null}
            <div style={{ color: "var(--text-mute)" }}>
              {n.date}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StepRowDesign({
  step,
  onOpen,
  onRunAgent,
  runAgentPending,
  onMatchAgent,
}: {
  step: StepRow;
  onOpen: () => void;
  onRunAgent: () => void;
  runAgentPending: boolean;
  onMatchAgent?: () => void;
}) {
  const s = step;
  const stateClass = s.state === "awaiting_review" ? "review" : s.state;
  const marker = s.state === "completed" ? "✓" : String(s.ord);
  const completedDate = s.completedAt
    ? new Date(s.completedAt).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      })
    : "";
  return (
    <div
      className={`step-row ${stateClass}`}
      data-testid={`step-row-${s.id}`}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter") onOpen();
      }}
    >
      <div className="step-marker">{marker}</div>
      <div className="step-body">
        <div className="top">
          <span className="ttl">{s.title}</span>
          <span className="type">{s.type}</span>
        </div>
        <div className="meta">
          <span>{s.displayOwner}</span>
          {s.isAutonomous && s.agentName ? (
            <span className="attr-badge">
              <span className="glyph">{stepTypeIcon(s.type)}</span>
              {s.agentName} · autonomous
            </span>
          ) : null}
          {s.isAutonomous && !s.agentName && onMatchAgent ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onMatchAgent(); }}
              style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                padding: "2px 8px", borderRadius: 99, border: "1px dashed var(--accent, #6366f1)",
                background: "color-mix(in srgb, var(--accent, #6366f1) 8%, transparent)",
                color: "var(--accent, #6366f1)", fontSize: 11, fontWeight: 600,
                cursor: "pointer", animation: "pulse 2s infinite",
              }}
            >
              ⚡ Match agent
            </button>
          ) : null}
        </div>
      </div>
      <div className="step-actions" onClick={(e) => e.stopPropagation()}>
        {s.state === "awaiting_review" ? (
          <>
            <button type="button" className="btn primary" onClick={onOpen}>
              Approve
            </button>
            <button type="button" className="btn" onClick={onOpen}>
              Request changes
            </button>
          </>
        ) : s.state === "running" ? (
          <>
            <span className="pill green">● running</span>
            <button type="button" className="btn" onClick={onOpen}>
              Open ↗
            </button>
          </>
        ) : s.state === "ready" ? (
          <>
            {s.isAutonomous && s.agentBindingId ? (
              <button
                type="button"
                className="btn primary"
                data-testid={`run-agent-${s.id}`}
                disabled={runAgentPending}
                onClick={onRunAgent}
                title={`Dispatch ${s.displayOwner} now (live HMAC-signed POST)`}
              >
                {runAgentPending ? "Dispatching…" : "⚡ Run agent"}
              </button>
            ) : (
              <button type="button" className="btn primary" onClick={onOpen}>
                Claim
              </button>
            )}
          </>
        ) : s.state === "pending" ? (
          s.isAutonomous && s.agentBindingId ? (
            <button
              type="button"
              className="btn"
              data-testid={`run-agent-${s.id}`}
              disabled={runAgentPending}
              onClick={onRunAgent}
              title={`Dispatch ${s.displayOwner} now (live HMAC-signed POST)`}
            >
              {runAgentPending ? "Dispatching…" : "⚡ Run agent"}
            </button>
          ) : (
            <span className="pill gray">Waiting on deps</span>
          )
        ) : s.state === "failed" ? (
          <>
            <span className="pill red">failed</span>
            {s.isAutonomous && s.agentBindingId ? (
              <button
                type="button"
                className="btn"
                data-testid={`run-agent-${s.id}`}
                disabled={runAgentPending}
                onClick={onRunAgent}
              >
                {runAgentPending ? "Dispatching…" : "⚡ Retry"}
              </button>
            ) : null}
          </>
        ) : s.state === "completed" ? (
          <span className="pill gray">
            Done{completedDate ? ` · ${completedDate}` : ""}
          </span>
        ) : (
          <span className="pill gray">{s.state.replace("_", " ")}</span>
        )}
      </div>
    </div>
  );
}

function StepDrawer({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      data-testid="step-drawer"
      role="dialog"
      aria-modal="true"
      aria-label={`Step detail: ${title}`}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 16, 22, 0.32)",
        zIndex: 900,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(560px, 92vw)",
          background: "var(--bg)",
          boxShadow: "-12px 0 32px rgba(0,0,0,0.18)",
          overflow: "auto",
        }}
      >
        <div
          style={{
            position: "sticky",
            top: 0,
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "12px 16px",
            borderBottom: "1px solid var(--border)",
            background: "var(--bg)",
            zIndex: 1,
          }}
        >
          <div style={{ flex: 1, fontWeight: 600 }}>{title}</div>
          <button
            type="button"
            className="btn"
            data-testid="step-drawer-close"
            onClick={onClose}
          >
            Close
          </button>
        </div>
        <div style={{ padding: 16 }}>{children}</div>
      </div>
    </div>
  );
}

function PlayWorkspaceInner() {
  const { workspaceId = "", playId = "" } = useParams<{
    workspaceId: string;
    playId: string;
  }>();
  const navigate = useNavigate();
  const me = useMe();
  const persona = usePersona(workspaceId);
  const query = usePlay(workspaceId, playId);
  const mutations = usePlayMutations(workspaceId, playId);
  const runAgent = useRunAgent(workspaceId, playId);

  const [tab, setTab] = useState<Tab>("steps");
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [confirm, setConfirm] = useState<ConfirmActionState | null>(null);
  const [matchingStepId, setMatchingStepId] = useState<string | null>(null);

  const onEvent = useCallback((e: WorkspaceEvent) => {
    setActivity((prev) => [eventToActivity(e, prev.length), ...prev].slice(0, 50));
  }, []);
  const { connectionStatus } = useWorkspaceEvents(workspaceId, onEvent);

  const detail = query.data;
  const steps: StepRow[] = detail?.steps ?? [];
  const events: StepEventRow[] = detail?.recentEvents ?? [];
  const plannerStore = usePlannerStore();

  const handleEdit = useCallback(() => {
    if (!detail) return;
    const { play, steps: s, dependencies } = detail;
    const entryId = play.roadmapEntryId ?? play.id;
    const edges = dependencies.map((d) => ({
      from: d.upstreamStepId,
      to: d.downstreamStepId,
    }));
    const projected = projectDraft({ id: play.id, steps: s, edges }, { initiativeTitle: play.archetype });
    plannerStore.patch(entryId, { stage: "editor", draftPlay: projected, editorView: "timeline", showDeps: true });
    navigate(`/workspaces/${workspaceId}/planner?roadmapEntryId=${encodeURIComponent(entryId)}&playId=${encodeURIComponent(play.id)}`);
  }, [detail, plannerStore, navigate, workspaceId]);

  const activeStep: StepRow | undefined = useMemo(() => {
    if (!steps.length) return undefined;
    if (selectedStepId) {
      const s = steps.find((x) => x.id === selectedStepId);
      if (s) return s;
    }
    return (
      steps.find((s) => s.state === "awaiting_review") ??
      steps.find((s) => s.state === "running") ??
      steps.find((s) => s.state === "ready") ??
      steps[0]
    );
  }, [steps, selectedStepId]);

  if (query.isLoading) {
    return (
      <div className="main-inner" data-testid="play-loading">
        Loading play…
      </div>
    );
  }
  if (query.isError || !detail) {
    return (
      <div className="main-inner" data-testid="play-error">
        Couldn’t load play: {query.error?.message ?? "unknown"}
      </div>
    );
  }

  const { play } = detail;
  const stepsDone = steps.filter((s) => s.state === "completed").length;
  const stepsTotal = steps.length;
  const elapsed = elapsedLabel(play);
  const window_ = windowLabel(play);

  const runConfirmedAction = async (state: ConfirmActionState, rationale?: string) => {
    try {
      switch (state.kind) {
        case "approve":
          await mutations.approve.mutateAsync();
          break;
        case "start":
          await mutations.start.mutateAsync();
          break;
        case "resume":
          await mutations.resume.mutateAsync();
          break;
        case "pause":
          await mutations.pause.mutateAsync(rationale ?? "");
          break;
        case "abandon":
          await mutations.abandon.mutateAsync(rationale ?? "");
          break;
      }
    } finally {
      setConfirm(null);
    }
  };

  return (
    <div
      className="main-inner"
      style={{ maxWidth: 1180 }}
      data-testid="play-workspace"
    >
      <OutcomeHeader
        outcomeStatement={play.outcomeStatement}
        successMetrics={play.successMetrics}
      />
      <div className="pw-head">
        <div>
          <div className="crumb">
            <a
              data-testid="play-back"
              onClick={() => navigate(`/workspaces/${workspaceId}/plays`)}
              style={{ cursor: "pointer" }}
            >
              Routines
            </a>
            {" / "}
            <span>{play.archetype}</span>
          </div>
          <h1 data-testid="play-title">{play.archetype}</h1>
          <div className="tags">
            <span className="archetype">{play.archetype}</span>
            <span
              className={`pill ${statusPill(play.state)}`}
              data-testid="play-state-pill"
            >
              {play.state}
            </span>
            {/* Source badge — only renders for non-XI sources so the
                XI-promoted default stays uncluttered. */}
            {query.data?.lineageSource === "template_direct" ? (
              <span
                className="pill"
                style={{
                  background: "color-mix(in srgb, var(--accent, #6366f1) 12%, transparent)",
                  color: "var(--accent, #6366f1)",
                }}
                title="Created directly from a template — no XI signal lineage. Outcomes don't write back to XI."
                data-testid="play-source-template"
              >
                ✦ Template
              </span>
            ) : null}
            <span className="pill">Owner: {play.ownerUserId}</span>
          </div>
        </div>
        <div className="pw-stats">
          <div className="item">
            <div className="v" data-testid="play-steps-stat">
              {stepsDone}/{stepsTotal}
            </div>
            <div className="l">Steps</div>
          </div>
          <div className="item">
            <div className="v">{elapsed}</div>
            <div className="l">Elapsed</div>
          </div>
          <div className="item">
            <div className="v">{window_}</div>
            <div className="l">Window</div>
          </div>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          padding: "8px 0",
          borderBottom: "1px solid var(--border)",
          marginBottom: 12,
        }}
      >
        <CxoActions
          state={play.state}
          persona={persona}
          onApprove={() => setConfirm({ kind: "approve", needsRationale: false })}
          onStart={() => setConfirm({ kind: "start", needsRationale: false })}
          onPause={() => setConfirm({ kind: "pause", needsRationale: true })}
          onResume={() => setConfirm({ kind: "resume", needsRationale: false })}
          onAbandon={() => setConfirm({ kind: "abandon", needsRationale: true })}
          onEdit={handleEdit}
          pending={
            mutations.approve.isPending ||
            mutations.start.isPending ||
            mutations.pause.isPending ||
            mutations.resume.isPending ||
            mutations.abandon.isPending
          }
        />
      </div>

      <div className="pw-tabs">
        {(["steps", "timeline", "outcome"] as Tab[]).map((k) => (
          <button
            key={k}
            type="button"
            className={`pw-tab ${tab === k ? "active" : ""}`}
            onClick={() => setTab(k)}
            data-testid={`play-tab-${k}`}
          >
            {k === "steps"
              ? "Steps"
              : k === "timeline"
                ? "Timeline"
                : "Outcome"}
            {k === "steps" ? (
              <span className="badge">{stepsTotal}</span>
            ) : null}
          </button>
        ))}
      </div>

      <div className="pw-grid">
        <div data-testid="play-main">
          {tab === "steps" ? (
            <div className="steps" data-testid="step-list">
              {steps.map((s) => (
                <StepRowDesign
                  key={s.id}
                  step={s}
                  onOpen={() => setSelectedStepId(s.id)}
                  onRunAgent={() => runAgent.mutate(s.id)}
                  runAgentPending={
                    runAgent.isPending && runAgent.variables === s.id
                  }
                  {...(s.isAutonomous && !s.agentName ? { onMatchAgent: () => setMatchingStepId(s.id) } : {})}
                />
              ))}
            </div>
          ) : null}

          {tab === "timeline" ? (
            <div data-testid="play-timeline">
              <PlayTimeline detail={detail} />
            </div>
          ) : null}

          {tab === "outcome" ? (
            <div
              data-testid="play-outcome"
              style={{
                fontSize: 13,
                color: "var(--text-3)",
                padding: 20,
                textAlign: "center",
              }}
            >
              Outcome panel — primary post-completion. Currently in run; live
              KPIs visible in side panel.
            </div>
          ) : null}

        </div>

        <div className="pw-side" data-testid="play-side">
          <div className="side-card" data-testid="live-outcome-card">
            <div className="h">
              <span>Live outcome</span>
              <span className="live" />
              <span className="right">daily capture</span>
            </div>
            <div className="outcome-card">
              <div className="lbl">Progress</div>
              <div className="row">
                <span className="v green">
                  {stepsDone}/{stepsTotal} steps
                </span>
                <span className="arr">
                  {stepsTotal === 0
                    ? "—"
                    : `${Math.round((stepsDone / stepsTotal) * 100)}% complete`}
                </span>
              </div>
              <div className="bar">
                <span
                  style={{
                    width:
                      stepsTotal === 0
                        ? "0%"
                        : `${Math.round((stepsDone / stepsTotal) * 100)}%`,
                  }}
                />
              </div>
              <div className="lo">
                <span>start</span>
                <span>target {window_}</span>
              </div>
            </div>
          </div>

          <div className="side-card" data-testid="activity-rail">
            <div className="h">
              <span>Activity</span>
              <span
                className="live"
                style={{
                  background:
                    connectionStatus === "open"
                      ? "var(--accent-2)"
                      : "var(--text-mute)",
                }}
              />
              <span className="right">last 24h</span>
            </div>
            {activity.length === 0 && events.length === 0 ? (
              <div
                className="side-act"
                data-testid="activity-empty"
                style={{ color: "var(--text-3)" }}
              >
                No activity yet.
              </div>
            ) : (
              <>
                {activity.map((a) => (
                  <div
                    key={a.id}
                    className="side-act entering"
                    data-testid={`activity-${a.id}`}
                  >
                    <div className="time">{a.time}</div>
                    <div>
                      <span className="who">{a.who}</span> {a.text}
                    </div>
                  </div>
                ))}
                {events.slice(0, 10).map((e) => (
                  <div
                    key={e.id}
                    className="side-act"
                    data-testid={`event-${e.id}`}
                  >
                    <div className="time">
                      {new Date(e.occurredAt).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                    <div>
                      <span className="who">{e.actorKind}</span>{" "}
                      {e.eventType}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </div>

      {selectedStepId && activeStep ? (
        <StepDrawer
          onClose={() => setSelectedStepId(null)}
          title={activeStep.title}
        >
          <StepDetail
            step={activeStep}
            play={play}
            workspaceId={workspaceId}
            currentUserId={me.data?.user.id ?? ""}
            persona={persona}
          />
        </StepDrawer>
      ) : null}

      {matchingStepId ? (() => {
        const matchStep = steps.find((s) => s.id === matchingStepId);
        return matchStep ? (
          <AgentMatchModal
            stepId={matchingStepId}
            stepTitle={matchStep.title}
            stepType={matchStep.type}
            workspaceId={workspaceId}
            onClose={() => setMatchingStepId(null)}
            onAssigned={() => { query.refetch(); setMatchingStepId(null); }}
          />
        ) : null;
      })() : null}

      {confirm ? (
        <ConfirmActionModal
          state={confirm}
          onCancel={() => setConfirm(null)}
          onConfirm={(rationale) => runConfirmedAction(confirm, rationale)}
        />
      ) : null}
    </div>
  );
}

interface ConfirmActionModalProps {
  state: ConfirmActionState;
  onCancel: () => void;
  onConfirm: (rationale?: string) => void;
}

function ConfirmActionModal({
  state,
  onCancel,
  onConfirm,
}: ConfirmActionModalProps) {
  const [rationale, setRationale] = useState("");
  const label = {
    approve: "Approve play?",
    start: "Start play?",
    pause: "Pause play?",
    resume: "Resume play?",
    abandon: "Abandon play?",
  }[state.kind];
  return (
    <div
      data-testid="confirm-action-modal"
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: 8,
          padding: 24,
          width: "min(420px, 92vw)",
          boxShadow: "0 12px 48px rgba(0,0,0,0.18)",
        }}
      >
        <h3 style={{ margin: 0, fontWeight: 600 }}>{label}</h3>
        {state.needsRationale ? (
          <textarea
            data-testid="confirm-rationale"
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            rows={3}
            placeholder="Rationale (required)"
            style={{
              marginTop: 12,
              width: "100%",
              border: "1px solid var(--border)",
              borderRadius: 6,
              padding: 8,
              fontFamily: "inherit",
            }}
          />
        ) : null}
        <div
          style={{
            display: "flex",
            gap: 8,
            justifyContent: "flex-end",
            marginTop: 16,
          }}
        >
          <button
            type="button"
            className="btn"
            onClick={onCancel}
            data-testid="confirm-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn primary"
            data-testid="confirm-submit"
            disabled={state.needsRationale && !rationale.trim()}
            onClick={() =>
              onConfirm(state.needsRationale ? rationale.trim() : undefined)
            }
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

function PlayWorkspaceErrorFallback({
  error,
  resetErrorBoundary,
}: {
  error: Error;
  resetErrorBoundary: () => void;
}) {
  return (
    <div className="main-inner" data-testid="play-workspace-error">
      <h1>Routine unavailable</h1>
      <div style={{ color: "var(--text-3)" }}>
        {error.message}
      </div>
      <button
        type="button"
        className="btn primary"
        style={{ marginTop: 12 }}
        onClick={resetErrorBoundary}
      >
        Reload surface
      </button>
    </div>
  );
}

export function PlayWorkspace() {
  return (
    <ErrorBoundary FallbackComponent={PlayWorkspaceErrorFallback}>
      <PlayWorkspaceInner />
    </ErrorBoundary>
  );
}
